import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  IonPage,
  IonContent,
  IonHeader,
  IonToolbar,
  IonButtons,
  IonButton,
  IonIcon,
  IonText,
  IonTitle,
  IonFab,
  IonFabButton,
  IonLabel,
  IonList,
  IonItem,
  IonModal,
  IonProgressBar,
  IonSpinner,
  IonInput,
  IonDatetime,
  IonPopover,
  IonTextarea,
  IonToast,
  IonSelect,
  IonSelectOption,
  IonCheckbox,
  IonToggle,
  IonChip,
  useIonRouter,
} from '@ionic/react';
import { timeOutline, locationOutline, personOutline, chevronForwardOutline as chevronForwardOutlineIcon, logoGoogle, closeOutline, notificationsOutline, peopleOutline, eyeOutline, trashOutline, createOutline } from 'ionicons/icons';
import BookingDetails from '../pages/bookingSystem/BookingDetails';
import { DataStorageService } from '@rsmaids/shared/services/DataStorageService';
import { DataServiceFactory } from '@rsmaids/shared/services/DataServiceFactory';
import { BookingItem } from '@rsmaids/shared/types/bookings';
import { serviceColorMap } from '@rsmaids/shared/types/services';
import { useAuth } from '../contexts';
import {
  chevronBackOutline,
  chevronForwardOutline,
  syncOutline,
  addOutline,
  menuOutline,
  calendarOutline,
  listOutline,
  closeCircleOutline,
  appsOutline,
  gridOutline,
  squareOutline,
  reorderThreeOutline,
  linkOutline,
  mailOutline,
  settingsOutline,
  checkmarkOutline,
} from 'ionicons/icons';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import 'swiper/css';
import styles from './MySchedule.module.scss';
import GcalTimePicker from '../components/GcalTimePicker';
import PortalDropdown from '../components/PortalDropdown';
import TodayCalendarIcon from '../components/TodayCalendarIcon';
import { CustomLiquidGlass } from '../components/LiquidGlass/CustomLiquidGlass';
import { googleCalendarService, type GoogleCalendarStatus, type GoogleCalendarEvent as GCalEvent, type CalendarListEntry } from '../services/googleCalendarService';
import { outlookCalendarService, type OutlookCalendarEvent, type OutlookAccount, type OutlookCalendarListEntry } from '../services/outlookCalendarService';
import { icsCalendarService, type IcsCalendar, type IcsCalendarEvent } from '../services/icsCalendarService';
import { useTranslation } from 'react-i18next';
import { intlLocale, weekdayNames } from '../i18n/locale';

interface CalendarEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  isAllDay?: boolean;
  location?: string;
  clientName?: string;
  color?: string;
  isGoogleEvent?: boolean;
  isOutlookEvent?: boolean;
  isIcsEvent?: boolean;
  gcalRaw?: GCalEvent;
  outlookRaw?: OutlookCalendarEvent;
  icsRaw?: IcsCalendarEvent;
  sourceKey?: string; // "${accountEmail}::${calendarId}" for visibility filtering
}

// Google Calendar event color (distinct from booking colors)
const GOOGLE_CALENDAR_COLOR = '#1a73e8';

const GCAL_AVATAR_COLORS = ['#1a73e8', '#34a853', '#ea4335', '#fbbc05', '#9c27b0', '#009688', '#ff5722', '#e91e63'];
function gcalAvatarColor(email: string): string {
  let hash = 0;
  for (const c of email) hash = (hash * 31 + c.charCodeAt(0)) & 0xffffffff;
  return GCAL_AVATAR_COLORS[Math.abs(hash) % GCAL_AVATAR_COLORS.length];
}

type ViewMode = 'month' | 'week' | 'day' | 'agenda';


function layoutEvents(events: CalendarEvent[]): Map<string, { col: number; numCols: number }> {
  const result = new Map<string, { col: number; numCols: number }>();
  if (events.length === 0) return result;

  const sorted = [...events].sort((a, b) =>
    a.start.getTime() !== b.start.getTime()
      ? a.start.getTime() - b.start.getTime()
      : b.end.getTime() - a.end.getTime()
  );

  const colEnds: number[] = [];
  const colMap = new Map<string, number>();

  for (const ev of sorted) {
    const t = ev.start.getTime();
    let placed = false;
    for (let c = 0; c < colEnds.length; c++) {
      if (t >= colEnds[c]) {
        colEnds[c] = ev.end.getTime();
        colMap.set(ev.id, c);
        placed = true;
        break;
      }
    }
    if (!placed) {
      colEnds.push(ev.end.getTime());
      colMap.set(ev.id, colEnds.length - 1);
    }
  }

  for (const ev of sorted) {
    const eStart = ev.start.getTime();
    const eEnd = ev.end.getTime();
    let maxCol = 0;
    for (const other of sorted) {
      if (other.start.getTime() < eEnd && other.end.getTime() > eStart) {
        const c = colMap.get(other.id) ?? 0;
        if (c > maxCol) maxCol = c;
      }
    }
    result.set(ev.id, { col: colMap.get(ev.id) ?? 0, numCols: maxCol + 1 });
  }

  return result;
}

const MySchedule: React.FC = () => {
  const { t: tr } = useTranslation();
  const router = useIonRouter();
  const { authState } = useAuth();
  const { user } = authState;
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState<ViewMode>(() => {
    const saved = localStorage.getItem('calendarViewMode');
    return (saved as ViewMode) || 'month';
  });
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [selectedDay, setSelectedDay] = useState<Date | null>(null);
  const [showDaySheet, setShowDaySheet] = useState(false);
  const [selectedBookingId, setSelectedBookingId] = useState<string | undefined>(undefined);
  const [showBookingDetails, setShowBookingDetails] = useState(false);
  const [selectedGcalEvent, setSelectedGcalEvent] = useState<GCalEvent | null>(null);
  const [showGcalDetail, setShowGcalDetail] = useState(false);
  // PortalDropdown state for the two former CardActionsPopover triggers
  // (add-calendar-gear, schedule-view-btn) — one ref/isOpen pair each, since
  // both are single fixed triggers, not per-row.
  const [showAddCalendarMenu, setShowAddCalendarMenu] = useState(false);
  const addCalendarTriggerRef = useRef<HTMLIonButtonElement>(null);
  const addCalendarPortalRef = useRef<HTMLDivElement>(null);
  const [showViewMenu, setShowViewMenu] = useState(false);
  const scheduleViewTriggerRef = useRef<HTMLIonButtonElement>(null);
  const scheduleViewPortalRef = useRef<HTMLDivElement>(null);
  // Edit form state for Google event detail modal
  const [gcalEditTitle, setGcalEditTitle] = useState('');
  const [gcalEditStart, setGcalEditStart] = useState('');
  const [gcalEditEnd, setGcalEditEnd] = useState('');
  const [gcalEditIsAllDay, setGcalEditIsAllDay] = useState(false);
  const [gcalStartDateOpen, setGcalStartDateOpen] = useState(false);
  const [gcalEndDateOpen, setGcalEndDateOpen] = useState(false);
  const [gcalStartTimeOpen, setGcalStartTimeOpen] = useState(false);
  const [gcalEndTimeOpen, setGcalEndTimeOpen] = useState(false);
  const [gcalEditLocation, setGcalEditLocation] = useState('');
  const [gcalEditDescription, setGcalEditDescription] = useState('');
  const [gcalEditColorId, setGcalEditColorId] = useState('');
  const [gcalEditTransparency, setGcalEditTransparency] = useState<'opaque' | 'transparent'>('opaque');
  const [gcalEditVisibility, setGcalEditVisibility] = useState<'default' | 'public' | 'private' | 'confidential'>('default');
  const [gcalEditAttendees, setGcalEditAttendees] = useState<Array<{ email: string; optional?: boolean }>>([]);
  const [gcalEditGuestsCanModify, setGcalEditGuestsCanModify] = useState(false);
  const [gcalEditGuestsCanInvite, setGcalEditGuestsCanInvite] = useState(true);
  const [gcalEditGuestsCanSeeOthers, setGcalEditGuestsCanSeeOthers] = useState(true);
  const [gcalEditReminderMinutes, setGcalEditReminderMinutes] = useState<number | null>(null);
  const [gcalEditReminderMethod, setGcalEditReminderMethod] = useState<'email' | 'popup'>('popup');
  const [gcalEditReminderUnit, setGcalEditReminderUnit] = useState<'minutes' | 'hours' | 'days'>('minutes');
  const [gcalEditReminderValue, setGcalEditReminderValue] = useState<string>('10');
  const [gcalEditNewGuest, setGcalEditNewGuest] = useState('');
  const [gcalEditCalendarId, setGcalEditCalendarId] = useState('');
  const [gcalEditRepeat, setGcalEditRepeat] = useState('NONE');
  const [gcalCalendarList, setGcalCalendarList] = useState<CalendarListEntry[]>([]);
  const [gcalSaving, setGcalSaving] = useState(false);
  const [gcalDeleting, setGcalDeleting] = useState(false);
  const [gcalConfirmDelete, setGcalConfirmDelete] = useState(false);
  const [gcalToast, setGcalToast] = useState<{ message: string; color: 'success' | 'danger' } | null>(null);
  const [gcalEditMode, setGcalEditMode] = useState(false);

  // Google Calendar integration
  const [gcalStatus, setGcalStatus] = useState<GoogleCalendarStatus | null>(null);
  const [gcalConnecting, setGcalConnecting] = useState(false);
  const [gcalEvents, setGcalEvents] = useState<CalendarEvent[]>([]);
  // Clear events the moment the account disconnects — computed during render
  // (React's "adjust state when a prop/state changes" pattern) rather than
  // as a synchronous setState call inside fetchGcalEvents's effect.
  const [gcalWasConnected, setGcalWasConnected] = useState(!!gcalStatus?.connected);
  if (!!gcalStatus?.connected !== gcalWasConnected) {
    setGcalWasConnected(!!gcalStatus?.connected);
    if (!gcalStatus?.connected) setGcalEvents([]);
  }
  const [gcalAccountCalendars, setGcalAccountCalendars] = useState<Record<string, CalendarListEntry[]>>({});
  // Clear account calendars the moment there's nothing connected to list —
  // computed during render rather than as a synchronous setState call at
  // the top of the effect below.
  const gcalHasConnectedAccounts = !!gcalStatus?.connected && !!gcalStatus.accounts.length;
  const [gcalHadAccounts, setGcalHadAccounts] = useState(gcalHasConnectedAccounts);
  if (gcalHasConnectedAccounts !== gcalHadAccounts) {
    setGcalHadAccounts(gcalHasConnectedAccounts);
    if (!gcalHasConnectedAccounts) setGcalAccountCalendars({});
  }
  const [gcalHiddenCalendars, setGcalHiddenCalendars] = useState<Set<string>>(() => {
    try {
      const saved = localStorage.getItem('gcalHiddenCalendars');
      return saved ? new Set(JSON.parse(saved) as string[]) : new Set();
    } catch { return new Set(); }
  });

  // Outlook Calendar
  const [outlookStatus, setOutlookStatus] = useState<{ connected: boolean; accounts: OutlookAccount[] } | null>(null);
  const [outlookConnecting] = useState(false);
  const [outlookEvents, setOutlookEvents] = useState<CalendarEvent[]>([]);
  // Clear events the moment the account disconnects — computed during
  // render rather than as a synchronous setState call inside
  // fetchOutlookEvents's effect.
  const [outlookWasConnected, setOutlookWasConnected] = useState(!!outlookStatus?.connected);
  if (!!outlookStatus?.connected !== outlookWasConnected) {
    setOutlookWasConnected(!!outlookStatus?.connected);
    if (!outlookStatus?.connected) setOutlookEvents([]);
  }
  const [outlookAccountCalendars, setOutlookAccountCalendars] = useState<Record<string, OutlookCalendarListEntry[]>>({});
  // Clear account calendars the moment there's nothing connected to list —
  // computed during render rather than as a synchronous setState call at
  // the top of the effect below.
  const outlookHasConnectedAccounts = !!outlookStatus?.connected && !!outlookStatus.accounts.length;
  const [outlookHadAccounts, setOutlookHadAccounts] = useState(outlookHasConnectedAccounts);
  if (outlookHasConnectedAccounts !== outlookHadAccounts) {
    setOutlookHadAccounts(outlookHasConnectedAccounts);
    if (!outlookHasConnectedAccounts) setOutlookAccountCalendars({});
  }

  // ICS / Apple Calendar
  const [icsCalendars, setIcsCalendars] = useState<IcsCalendar[]>([]);
  const [icsEvents, setIcsEvents] = useState<CalendarEvent[]>([]);
  // Clear events the moment there are no ICS calendars left — computed
  // during render rather than as a synchronous setState call inside
  // fetchIcsEvents's effect.
  const [icsHadCalendars, setIcsHadCalendars] = useState(icsCalendars.length > 0);
  if ((icsCalendars.length > 0) !== icsHadCalendars) {
    setIcsHadCalendars(icsCalendars.length > 0);
    if (icsCalendars.length === 0) setIcsEvents([]);
  }

  const [showAddIcsModal, setShowAddIcsModal] = useState(false);
  const [icsFormName, setIcsFormName] = useState('');
  const [icsFormUrl, setIcsFormUrl] = useState('');
  const [icsFormColor, setIcsFormColor] = useState('#1a73e8');
  const [icsFormType, setIcsFormType] = useState<'ics' | 'apple'>('ics');
  const [icsFormSaving, setIcsFormSaving] = useState(false);
  const [icsFormError, setIcsFormError] = useState('');
  const [showCalendarPanel, setShowCalendarPanel] = useState(false);
  const swiperRef = useRef<SwiperType | null>(null);
  const dayHeaderTrackRef = useRef<HTMLIonTextElement | null>(null);
  const contentRef = useRef<HTMLIonContentElement>(null);
  const [allDayRowVisible, setAllDayRowVisible] = useState(false);
  const gcalFetchedWeekRef = useRef<string>('');

  const syncDayHeaderTrackToSwiper = (swiper?: SwiperType | null) => {
    const s = swiper ?? swiperRef.current;
    const el = dayHeaderTrackRef.current;
    if (!s || !el) return;
    // Swiper translate is in px; mirroring it gives true "slide" movement.
    el.style.transform = `translate3d(${s.translate}px, 0, 0)`;
  };

  const setDayHeaderTrackTransition = (durationMs: number) => {
    const el = dayHeaderTrackRef.current;
    if (!el) return;
    el.style.transitionProperty = 'transform';
    el.style.transitionDuration = `${Math.max(0, durationMs)}ms`;
    el.style.transitionTimingFunction = 'ease';
  };

  // Save view mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('calendarViewMode', viewMode);
  }, [viewMode]);

  // Check Google Calendar connection status on mount and when app comes back to foreground
  useEffect(() => {
    const refreshStatus = () => googleCalendarService.getStatus()
      .then((s) => { console.log('[GCal] status:', JSON.stringify(s)); setGcalStatus(s); })
      .catch((err) => console.warn('Could not check Google Calendar status:', err));
    refreshStatus();
    const onVisible = () => { if (document.visibilityState === 'visible') refreshStatus(); };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  // Fetch Google Calendar events whenever date range or connection changes.
  // Written as a promise chain (not async/await) so setGcalEvents calls
  // aren't traced as "synchronous setState in an effect" when this is
  // called directly from the mount/visibility effect below — disconnected
  // accounts are handled separately above via the render-time reset instead
  // of an early return here.
  const fetchGcalEvents = useCallback(() => {
    if (!gcalStatus?.connected) return Promise.resolve();

    let rangeStart: Date;
    let rangeEnd: Date;

    if (viewMode === 'month') {
      rangeStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      rangeEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
    } else if (viewMode === 'week') {
      const day = currentDate.getDay();
      rangeStart = new Date(currentDate);
      rangeStart.setDate(currentDate.getDate() - day);
      rangeStart.setHours(0, 0, 0, 0);
      rangeEnd = new Date(rangeStart);
      rangeEnd.setDate(rangeStart.getDate() + 6);
      rangeEnd.setHours(23, 59, 59, 999);
    } else if (viewMode === 'day') {
      rangeStart = new Date(currentDate);
      rangeStart.setHours(0, 0, 0, 0);
      rangeEnd = new Date(currentDate);
      rangeEnd.setHours(23, 59, 59, 999);
    } else {
      rangeStart = new Date();
      rangeStart.setHours(0, 0, 0, 0);
      rangeEnd = new Date(rangeStart);
      rangeEnd.setDate(rangeStart.getDate() + 30);
    }

    return googleCalendarService.getEvents(rangeStart, rangeEnd)
      .then((result) => {
        if (result.tokenRevoked) {
          setGcalStatus({ connected: false, accounts: [] });
          setGcalEvents([]);
          return;
        }
        if (result.events.length > 0) {
          const s = result.events[0];
          console.log('[GCal] raw start string:', s.start, '→ Date:', new Date(s.start).toLocaleTimeString(intlLocale()));
        }
        const GCAL_COLOR_MAP_LOCAL: Record<string, string> = { '1': '#D50000', '2': '#E67C73', '3': '#F4511E', '4': '#F6BF26', '5': '#33B679', '6': '#0B8043', '7': '#039BE5', '8': '#3F51B5', '9': '#7986CB', '10': '#8E24AA', '11': '#616161' };
        const mapped: CalendarEvent[] = result.events.map((e) => ({
          id: e.id,
          title: e.title,
          start: new Date(e.start),
          end: new Date(e.end),
          isAllDay: e.isAllDay,
          location: e.location,
          color: (e.colorId ? GCAL_COLOR_MAP_LOCAL[e.colorId] : undefined) || e.calendarColor || GOOGLE_CALENDAR_COLOR,
          isGoogleEvent: true,
          gcalRaw: e,
          sourceKey: e.accountEmail && e.calendarId ? `${e.accountEmail}::${e.calendarId}` : undefined,
        }));
        gcalFetchedWeekRef.current = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`;
        setGcalEvents(mapped);
      })
      .catch((err) => {
        console.warn('Could not fetch Google Calendar events:', err);
      });
  }, [gcalStatus, currentDate, viewMode]);

  useEffect(() => {
    fetchGcalEvents();
    let lastFetch = Date.now();
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastFetch > 30_000) {
        lastFetch = Date.now();
        fetchGcalEvents();
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [fetchGcalEvents]);

  const handleGcalConnect = async () => {
    setGcalConnecting(true);
    try {
      const url = await googleCalendarService.getOAuthUrl();
      // Navigate in the same tab – Google will redirect back to /google-calendar-callback
      window.location.href = url;
    } catch (err) {
      console.error('Google Calendar connect error:', err);
      setGcalConnecting(false);
    }
  };

  const handleGcalDisconnect = async (accountEmail: string) => {
    try {
      await googleCalendarService.disconnect(accountEmail);
      const updated = await googleCalendarService.getStatus();
      setGcalStatus(updated);
      if (!updated.connected) setGcalEvents([]);
      else fetchGcalEvents();
    } catch (err) {
      console.error('Google Calendar disconnect error:', err);
    }
  };

  // ─── Outlook Calendar ─────────────────────────────────────────────────────

  useEffect(() => {
    outlookCalendarService.getStatus()
      .then(setOutlookStatus)
      .catch((err) => console.warn('Could not check Outlook status:', err));
  }, []);

  useEffect(() => {
    if (!outlookHasConnectedAccounts || !outlookStatus) return;
    Promise.all(
      outlookStatus.accounts.map(async (acct) => {
        try {
          const cals = await outlookCalendarService.getCalendarList(acct.email);
          return [acct.email, cals] as [string, OutlookCalendarListEntry[]];
        } catch {
          return [acct.email, [] as OutlookCalendarListEntry[]] as [string, OutlookCalendarListEntry[]];
        }
      })
    ).then((results) => setOutlookAccountCalendars(Object.fromEntries(results)));
  }, [outlookStatus, outlookHasConnectedAccounts]);

  // Promise chain (not async/await) so setOutlookEvents isn't traced as
  // "synchronous setState in an effect" when called directly from the
  // mount effect below — disconnected accounts are handled separately above
  // via the render-time reset instead of an early return here.
  const fetchOutlookEvents = useCallback(() => {
    if (!outlookStatus?.connected) return Promise.resolve();
    let rangeStart: Date, rangeEnd: Date;
    if (viewMode === 'month') {
      rangeStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      rangeEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
    } else if (viewMode === 'week') {
      rangeStart = new Date(currentDate); rangeStart.setDate(currentDate.getDate() - currentDate.getDay()); rangeStart.setHours(0,0,0,0);
      rangeEnd = new Date(rangeStart); rangeEnd.setDate(rangeStart.getDate() + 6); rangeEnd.setHours(23,59,59,999);
    } else if (viewMode === 'day') {
      rangeStart = new Date(currentDate); rangeStart.setHours(0,0,0,0);
      rangeEnd = new Date(currentDate); rangeEnd.setHours(23,59,59,999);
    } else {
      rangeStart = new Date(); rangeStart.setHours(0,0,0,0);
      rangeEnd = new Date(rangeStart); rangeEnd.setDate(rangeStart.getDate() + 30);
    }
    return outlookCalendarService.getEvents(rangeStart, rangeEnd)
      .then((evs) => {
        const mapped: CalendarEvent[] = evs.map((e) => ({
          id: e.id,
          title: e.title,
          start: new Date(e.start),
          end: new Date(e.end),
          location: e.location,
          color: e.calendarColor || '#0078D4',
          isOutlookEvent: true,
          outlookRaw: e,
          sourceKey: `${e.accountEmail}::${e.calendarId}`,
        }));
        setOutlookEvents(mapped);
      })
      .catch((err) => {
        console.warn('Could not fetch Outlook events:', err);
      });
  }, [outlookStatus, currentDate, viewMode]);

  useEffect(() => { fetchOutlookEvents(); }, [fetchOutlookEvents]);

  const handleOutlookConnect = async () => {
    try {
      await outlookCalendarService.connect();
      const status = await outlookCalendarService.getStatus();
      setOutlookStatus(status);
      fetchOutlookEvents();
    } catch (err) {
      console.error('Outlook connect error:', err);
    }
  };

  const handleOutlookDisconnect = async (accountEmail: string) => {
    try {
      await outlookCalendarService.disconnect(accountEmail);
      const updated = await outlookCalendarService.getStatus();
      setOutlookStatus(updated);
      if (!updated.connected) setOutlookEvents([]);
      else fetchOutlookEvents();
    } catch (err) {
      console.error('Outlook disconnect error:', err);
    }
  };

  // ─── ICS / Apple Calendar ──────────────────────────────────────────────────

  // Promise chain (not async/await) so setIcsCalendars isn't traced as
  // "synchronous setState in an effect" when called directly from the
  // mount effect below.
  const loadIcsCalendars = useCallback(() => {
    return icsCalendarService.list()
      .then((list) => { setIcsCalendars(list); })
      .catch((err) => { console.warn('Could not load ICS calendars:', err); });
  }, []);

  useEffect(() => { loadIcsCalendars(); }, [loadIcsCalendars]);

  // Promise chain (not async/await) so setIcsEvents isn't traced as
  // "synchronous setState in an effect" when called directly from the
  // mount effect below — the no-calendars case is handled separately above
  // via the render-time reset instead of an early return here.
  const fetchIcsEvents = useCallback(() => {
    if (!icsCalendars.length) return Promise.resolve();
    let rangeStart: Date, rangeEnd: Date;
    if (viewMode === 'month') {
      rangeStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      rangeEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
    } else if (viewMode === 'week') {
      rangeStart = new Date(currentDate); rangeStart.setDate(currentDate.getDate() - currentDate.getDay()); rangeStart.setHours(0,0,0,0);
      rangeEnd = new Date(rangeStart); rangeEnd.setDate(rangeStart.getDate() + 6); rangeEnd.setHours(23,59,59,999);
    } else if (viewMode === 'day') {
      rangeStart = new Date(currentDate); rangeStart.setHours(0,0,0,0);
      rangeEnd = new Date(currentDate); rangeEnd.setHours(23,59,59,999);
    } else {
      rangeStart = new Date(); rangeStart.setHours(0,0,0,0);
      rangeEnd = new Date(rangeStart); rangeEnd.setDate(rangeStart.getDate() + 30);
    }
    return icsCalendarService.getEvents(rangeStart, rangeEnd)
      .then((evs) => {
        const mapped: CalendarEvent[] = evs.map((e) => ({
          id: e.id,
          title: e.title,
          start: new Date(e.start),
          end: new Date(e.end),
          location: e.location,
          color: e.calendarColor || '#1a73e8',
          isIcsEvent: true,
          icsRaw: e,
          sourceKey: `ics::${e.calendarId}`,
        }));
        setIcsEvents(mapped);
      })
      .catch((err) => {
        console.warn('Could not fetch ICS events:', err);
      });
  }, [icsCalendars, currentDate, viewMode]);

  useEffect(() => { fetchIcsEvents(); }, [fetchIcsEvents]);

  const handleAddIcsCalendar = async () => {
    if (!icsFormName.trim() || !icsFormUrl.trim()) return;
    setIcsFormSaving(true);
    setIcsFormError('');
    try {
      await icsCalendarService.add(icsFormName.trim(), icsFormUrl.trim(), icsFormColor, icsFormType);
      await loadIcsCalendars();
      setShowAddIcsModal(false);
      setIcsFormName(''); setIcsFormUrl(''); setIcsFormColor('#1a73e8'); setIcsFormType('ics');
    } catch (err: unknown) {
      setIcsFormError(err instanceof Error ? err.message : tr('pages.MySchedule.couldNotAddCalendarCheckThe'));
    } finally {
      setIcsFormSaving(false);
    }
  };

  const handleRemoveIcsCalendar = async (calendarId: string) => {
    try {
      await icsCalendarService.remove(calendarId);
      await loadIcsCalendars();
      setIcsEvents((prev) => prev.filter((ev) => ev.icsRaw?.calendarId !== calendarId));
    } catch (err) {
      console.error('Remove ICS error:', err);
    }
  };

  // Fetch individual calendar lists for each connected account
  useEffect(() => {
    if (!gcalHasConnectedAccounts || !gcalStatus) return;
    Promise.all(
      gcalStatus.accounts.map(async (account) => {
        try {
          const calendars = await googleCalendarService.getCalendarList(account.email);
          return [account.email, calendars] as [string, CalendarListEntry[]];
        } catch {
          return [account.email, [] as CalendarListEntry[]] as [string, CalendarListEntry[]];
        }
      })
    ).then((results) => setGcalAccountCalendars(Object.fromEntries(results)));
  }, [gcalStatus, gcalHasConnectedAccounts]);

  const toggleCalendarVisibility = (accountEmail: string, calendarId: string) => {
    const key = `${accountEmail}::${calendarId}`;
    setGcalHiddenCalendars((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      try { localStorage.setItem('gcalHiddenCalendars', JSON.stringify([...next])); } catch { /* ignore */ }
      return next;
    });
  };

  // Fetch real bookings for the visible date range, filtered to this staff
  // member. Written as a promise chain (not async/await) so setEvents isn't
  // traced as "synchronous setState in an effect" when called directly from
  // the mount effect below. setIsRefreshing(true) is the manual-refresh
  // handler's own responsibility (see handleRefresh below) rather than a
  // parameter here, so this function has no synchronous setState call of
  // its own that the mount effect could be seen as reaching.
  const fetchEvents = useCallback(() => {
    const dataService = DataServiceFactory.getService();
    const staffName = user?.name || user?.firstName || '';

    let rangeStart: Date;
    let rangeEnd: Date;

    if (viewMode === 'month') {
      rangeStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      rangeEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0, 23, 59, 59);
    } else if (viewMode === 'week') {
      const day = currentDate.getDay();
      rangeStart = new Date(currentDate);
      rangeStart.setDate(currentDate.getDate() - day);
      rangeStart.setHours(0, 0, 0, 0);
      rangeEnd = new Date(rangeStart);
      rangeEnd.setDate(rangeStart.getDate() + 6);
      rangeEnd.setHours(23, 59, 59, 999);
    } else if (viewMode === 'day') {
      rangeStart = new Date(currentDate);
      rangeStart.setHours(0, 0, 0, 0);
      rangeEnd = new Date(currentDate);
      rangeEnd.setHours(23, 59, 59, 999);
    } else {
      rangeStart = new Date();
      rangeStart.setHours(0, 0, 0, 0);
      rangeEnd = new Date(rangeStart);
      rangeEnd.setDate(rangeStart.getDate() + 30);
    }

    return dataService.bookings.getBookingsAndBlockedSlotsByDateRange(rangeStart, rangeEnd, [])
      .then((bookings) => {
        const filtered = (bookings as BookingItem[]).filter((b) =>
          !staffName || (b.staff && b.staff.toLowerCase().includes(staffName.toLowerCase()))
        );

        const mapped: CalendarEvent[] = filtered.map((b) => ({
          id: b.id,
          title: b.title,
          start: b.startTime instanceof Date ? b.startTime : new Date(b.startTime),
          end: b.endTime instanceof Date ? b.endTime : new Date(b.endTime),
          location: b.location || b.contact?.address,
          clientName: b.contact?.name,
          color: serviceColorMap[b.serviceType as keyof typeof serviceColorMap] || 'var(--ion-color-primary)',
        }));

        setEvents(mapped);
      })
      .catch((error) => {
        console.error('Error fetching schedule events:', error);
      })
      .finally(() => {
        setIsRefreshing(false);
      });
  }, [currentDate, viewMode, user]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Merge booking events with all calendar sources, filtering hidden calendars
  const allEvents = useMemo(() => {
    const visible = (ev: CalendarEvent) =>
      !ev.sourceKey || !gcalHiddenCalendars.has(ev.sourceKey);
    return [
      ...events,
      ...gcalEvents.filter(visible),
      ...outlookEvents.filter(visible),
      ...icsEvents.filter(visible),
    ];
  }, [events, gcalEvents, outlookEvents, icsEvents, gcalHiddenCalendars]);


  const timeSlots = useMemo(() => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
      slots.push(hour);
    }
    return slots;
  }, []);

  // Calculate date for a given slide offset
  const getDateForOffset = (offset: number) => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(currentDate.getMonth() + offset);
    } else if (viewMode === 'week') {
      newDate.setDate(currentDate.getDate() + (offset * 7));
    } else if (viewMode === 'day') {
      newDate.setDate(currentDate.getDate() + offset);
    }
    return newDate;
  };

  const navigatePeriod = (direction: 'prev' | 'next') => {
    const newDate = new Date(currentDate);
    if (viewMode === 'month') {
      newDate.setMonth(currentDate.getMonth() + (direction === 'next' ? 1 : -1));
    } else if (viewMode === 'week') {
      newDate.setDate(currentDate.getDate() + (direction === 'next' ? 7 : -7));
    } else if (viewMode === 'day') {
      newDate.setDate(currentDate.getDate() + (direction === 'next' ? 1 : -1));
    }
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchEvents();
    fetchGcalEvents();
  };

  const selectView = (view: ViewMode) => {
    setViewMode(view);
  };

  // Swiper slide change handler
  const handleSlideChange = (swiper: SwiperType) => {
    const newIndex = swiper.activeIndex;

    if (newIndex === 0) {
      // Swiped to previous
      navigatePeriod('prev');
      // Reset to middle slide without animation
      setTimeout(() => {
        swiper.slideTo(1, 0);
        setDayHeaderTrackTransition(0);
        syncDayHeaderTrackToSwiper(swiper);
      }, 0);
    } else if (newIndex === 2) {
      // Swiped to next
      navigatePeriod('next');
      // Reset to middle slide without animation
      setTimeout(() => {
        swiper.slideTo(1, 0);
        setDayHeaderTrackTransition(0);
        syncDayHeaderTrackToSwiper(swiper);
      }, 0);
    }
  };

  const formatHeader = () => {
    if (viewMode === 'month') {
      return currentDate.toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' });
    } else if (viewMode === 'week') {
      return currentDate.toLocaleDateString(intlLocale(), { month: 'long', year: 'numeric' });
    } else if (viewMode === 'day') {
      return currentDate.toLocaleDateString(intlLocale(), { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
    }
    return tr('pages.MySchedule.agenda');
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const toLocalDateStr = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;

  const getEventsForDate = (date: Date) => {
    const dateStr = toLocalDateStr(date);
    return allEvents.filter(event => toLocalDateStr(event.start) === dateStr);
  };

  // Each slot = 60px cell + 2px gap = 62px per hour
  const SLOT_PX = 62;
  const GRID_HEIGHT = 24 * 60 + 23 * 2; // 1486px

  const getEventPosition = (event: CalendarEvent) => {
    const startMinutes = event.start.getHours() * 60 + event.start.getMinutes();
    let endMinutes = event.end.getHours() * 60 + event.end.getMinutes();
    if (endMinutes <= startMinutes) endMinutes = 24 * 60;
    const top = (startMinutes / 60) * SLOT_PX;
    const rawHeight = ((endMinutes - startMinutes) / 60) * SLOT_PX;
    // When the end falls exactly on an hour boundary, the formula overshoots by
    // the inter-cell gap (SLOT_PX - 60 = 2px). Subtract it so the block ends
    // flush with the bottom of the last occupied cell, not 2px into the gap.
    const gapOvershoot = endMinutes % 60 === 0 ? SLOT_PX - 60 : 0;
    const height = Math.max(Math.min(rawHeight - gapOvershoot, GRID_HEIGHT - top), 20);
    return { top, height };
  };

  const formatTimeDisplay = (isoStr: string): string => {
    const t = isoStr.substring(11, 16);
    if (!t) return '';
    const [h, m] = t.split(':').map(Number);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
    return `${hour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')} ${ampm}`;
  };

  const formatDateDisplay = (iso: string) => {
    if (!iso || iso.length < 10) return iso;
    const [y, m, d] = iso.substring(0, 10).split('-');
    return `${m}/${d}/${y}`;
  };

  const formatTime = (hour: number) => {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour} ${ampm}`;
  };

  // Helper to get week days for any date
  const getWeekDaysForDate = (date: Date) => {
    const start = new Date(date);
    const day = start.getDay();
    start.setDate(start.getDate() - day);

    const days = [];
    for (let i = 0; i < 7; i++) {
      const dayDate = new Date(start);
      dayDate.setDate(start.getDate() + i);
      days.push(dayDate);
    }
    return days;
  };

  // Showing the row is a pure function of allEvents/currentDate, so it's
  // computed directly during render (React's "adjust state when a
  // dependency changes" pattern) instead of via an effect — only the hide
  // side stays effect-driven below, since it genuinely depends on an
  // external async signal (gcalFetchedWeekRef, populated once the gcal
  // fetch for this week actually settles).
  const weekDays = getWeekDaysForDate(currentDate);
  const hasAllDayEventThisWeek = allEvents.some(
    e => e.isAllDay && weekDays.some(d => toLocalDateStr(e.start) === toLocalDateStr(d))
  );
  if (hasAllDayEventThisWeek && !allDayRowVisible) {
    setAllDayRowVisible(true);
  }

  useEffect(() => {
    if (hasAllDayEventThisWeek) return;
    // Only hide once the gcal fetch for this specific week has completed,
    // so stale-cleared events during week transitions don't flash the row away.
    const weekKey = `${currentDate.getFullYear()}-${currentDate.getMonth()}-${currentDate.getDate()}`;
    const fetchDone = !gcalStatus?.connected || gcalFetchedWeekRef.current === weekKey;
    if (fetchDone) setAllDayRowVisible(false);
  }, [hasAllDayEventThisWeek, currentDate, gcalStatus]);

  // Helper to get month days for any date
  const getMonthDaysForDate = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDay = firstDay.getDay();

    const days = [];
    // Previous month days
    for (let i = 0; i < startDay; i++) {
      const dayDate = new Date(year, month, -startDay + i + 1);
      days.push({ date: dayDate, isCurrentMonth: false });
    }
    // Current month days
    for (let i = 1; i <= lastDay.getDate(); i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    // Next month days to fill the grid
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    return days;
  };

  // Render content for each view mode using the current date + offset
  const renderViewContent = (offset: number) => {
    const dateForSlide = getDateForOffset(offset);

    switch (viewMode) {
      case 'month':
        return renderMonthContent(dateForSlide);
      case 'week':
        return renderWeekContent(dateForSlide);
      case 'day':
        return renderDayContent(dateForSlide);
      default:
        return null;
    }
  };

  const openDaySheet = (date: Date) => {
    setSelectedDay(date);
    setShowDaySheet(true);
    fetchGcalEvents();
  };

  // Helper: format ISO string to datetime-local input value (YYYY-MM-DDTHH:mm)
  const toDatetimeLocal = (iso: string) => {
    const d = new Date(iso);
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const openGcalDetail = (raw: GCalEvent) => {
    fetchGcalEvents();
    console.log('[GCal] openGcalDetail organizerEmail:', raw.organizerEmail, 'calendarId:', raw.calendarId);
    setSelectedGcalEvent(raw);
    setGcalEditTitle(raw.title);
    setGcalEditIsAllDay(raw.isAllDay);
    setGcalEditStart(raw.isAllDay ? raw.start.substring(0, 10) : toDatetimeLocal(raw.start));
    setGcalEditEnd(raw.isAllDay ? (() => {
      // Google end is exclusive (day after); subtract 1 day for display
      const d = new Date(raw.end.substring(0, 10) + 'T00:00:00');
      d.setDate(d.getDate() - 1);
      return d.toISOString().substring(0, 10);
    })() : toDatetimeLocal(raw.end));
    setGcalEditLocation(raw.location ?? '');
    setGcalEditDescription(raw.description ?? '');
    setGcalEditColorId(raw.colorId ?? '');
    setGcalEditTransparency((raw.transparency as 'opaque' | 'transparent') ?? 'opaque');
    setGcalEditVisibility((raw.visibility as 'default' | 'public' | 'private' | 'confidential') ?? 'default');
    setGcalEditAttendees((raw.attendees ?? []).map((a) => ({ email: a.email, optional: a.optional })));
    setGcalEditGuestsCanModify(raw.guestsCanModify ?? false);
    setGcalEditGuestsCanInvite(raw.guestsCanInviteOthers ?? true);
    setGcalEditGuestsCanSeeOthers(raw.guestsCanSeeOtherGuests ?? true);
    const firstOverride = raw.reminders?.overrides?.[0];
    setGcalEditReminderMinutes(raw.reminders?.useDefault ? null : (firstOverride?.minutes ?? null));
    setGcalEditReminderMethod((firstOverride?.method as 'email' | 'popup') ?? 'popup');
    // Populate the new reminder value/unit fields
    const mins = raw.reminders?.useDefault ? null : (firstOverride?.minutes ?? null);
    if (mins === null) {
      setGcalEditReminderValue('10');
      setGcalEditReminderUnit('minutes');
    } else if (mins % 1440 === 0) {
      setGcalEditReminderValue(String(mins / 1440));
      setGcalEditReminderUnit('days');
    } else if (mins % 60 === 0) {
      setGcalEditReminderValue(String(mins / 60));
      setGcalEditReminderUnit('hours');
    } else {
      setGcalEditReminderValue(String(mins));
      setGcalEditReminderUnit('minutes');
    }
    // Repeat: detect RRULE from recurrence
    const rrule = raw.recurrence?.find((r) => r.startsWith('RRULE:')) ?? 'NONE';
    setGcalEditRepeat(rrule === 'NONE' ? 'NONE' : rrule);
    setGcalEditCalendarId(raw.calendarId ?? '');
    setGcalEditNewGuest('');
    setGcalConfirmDelete(false);
    setGcalEditMode(false);
    setShowGcalDetail(true);
    // Fetch calendar list in background
    if (gcalCalendarList.length === 0 && raw.accountEmail) {
      googleCalendarService.getCalendarList(raw.accountEmail).then(setGcalCalendarList).catch(() => {});
    }
  };

  const openBookingDirect = (eventId: string) => {
    const event = allEvents.find(e => e.id === eventId);
    if (!event) return;
    if (event.isGoogleEvent && event.gcalRaw) {
      openGcalDetail(event.gcalRaw);
      return;
    }
    DataStorageService.storeData(`booking-${eventId}`, event, false);
    setSelectedBookingId(eventId);
    setShowBookingDetails(true);
  };

  const renderMonthContent = (date: Date) => {
    const monthDaysForDate = getMonthDaysForDate(date);
    return (
      <div className={styles.monthGrid}>
        {monthDaysForDate.map((item, index) => {
          const dayEvents = getEventsForDate(item.date);
          return (
            <div
              key={index}
              className={`${styles.monthDay} ${!item.isCurrentMonth ? styles.otherMonth : ''} ${isToday(item.date) ? styles.today : ''}`}
              onClick={() => openDaySheet(item.date)}
            >
              <div className={styles.monthDayNumber}>{item.date.getDate()}</div>
              {dayEvents.length > 0 && (
                <div className={styles.monthDayEvents}>
                  {dayEvents.slice(0, 3).map((event) => (
                    <div key={event.id} className={styles.monthEvent} style={{ backgroundColor: event.color }}>
                      {event.title}
                    </div>
                  ))}
                  {dayEvents.length > 3 && (
                    <div className={styles.monthMore}>+{dayEvents.length - 3}{tr('pages.MySchedule.more')}</div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderWeekContent = (date: Date) => {
    const weekDaysForDate = getWeekDaysForDate(date);
    return (
      <div className={styles.weekView}>
        <div className={styles.calendarGrid}>
          <div className={styles.timeColumn}>
            {timeSlots.map((hour) => (
              <div key={hour} className={styles.timeSlot}>
                <span className={styles.timeLabel}>{formatTime(hour)}</span>
              </div>
            ))}
          </div>

          <div className={styles.daysContainer}>
            {weekDaysForDate.map((day, dayIndex) => {
              const timedEvents = getEventsForDate(day).filter(e => !e.isAllDay);
              const layouts = layoutEvents(timedEvents);
              return (
                <div
                  key={dayIndex}
                  className={`${styles.dayColumn} ${isToday(day) ? styles.todayColumn : ''}`}
                  onClick={() => openDaySheet(day)}
                >
                  {timeSlots.map((hour) => (
                    <div key={hour} className={styles.gridCell}></div>
                  ))}
                  {timedEvents.map((event) => {
                    const { top, height } = getEventPosition(event);
                    const { col, numCols } = layouts.get(event.id) ?? { col: 0, numCols: 1 };
                    const colW = 100 / numCols;
                    return (
                      <div
                        key={event.id}
                        className={styles.event}
                        style={{
                          top: `${top}px`,
                          height: `${height}px`,
                          backgroundColor: event.color,
                          left: `calc(${col * colW}% + 2px)`,
                          width: `calc(${colW}% - 4px)`,
                          right: 'unset',
                        }}
                      >
                        <span className={styles.eventTime}>
                          {event.start.toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true })}
                        </span>
                        <span className={styles.eventTitle}>{event.title}</span>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderDayContent = (date: Date) => (
    <div className={styles.dayView}>
      <div className={styles.calendarGrid}>
        <div className={styles.timeColumn}>
          {timeSlots.map((hour) => (
            <div key={hour} className={styles.timeSlot}>
              <span className={styles.timeLabel}>{formatTime(hour)}</span>
            </div>
          ))}
        </div>

        <div className={styles.singleDayContainer}>
          <div
            className={`${styles.dayColumn} ${styles.todayColumn}`}
            onClick={() => openDaySheet(date)}
          >
            {timeSlots.map((hour) => (
              <div key={hour} className={styles.gridCell}></div>
            ))}
            {(() => {
              const timedDayEvents = getEventsForDate(date).filter(e => !e.isAllDay);
              const layouts = layoutEvents(timedDayEvents);
              return timedDayEvents.map((event) => {
                const { top, height } = getEventPosition(event);
                const { col, numCols } = layouts.get(event.id) ?? { col: 0, numCols: 1 };
                const colW = 100 / numCols;
                return (
                  <div
                    key={event.id}
                    className={styles.event}
                    style={{
                      top: `${top}px`,
                      height: `${height}px`,
                      backgroundColor: event.color,
                      left: `calc(${col * colW}% + 2px)`,
                      width: `calc(${colW}% - 4px)`,
                      right: 'unset',
                    }}
                  >
                    <span className={styles.eventTime}>
                      {event.start.toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true })}
                    </span>
                    <span className={styles.eventTitle}>{event.title}</span>
                    {event.location && <span className={styles.eventClient}>{event.location}</span>}
                  </div>
                );
              });
            })()}
          </div>
        </div>
      </div>
    </div>
  );

  const renderAgendaView = () => (
    <div className={styles.agendaView}>
      {allEvents.map((event) => (
        <div key={event.id} className={styles.agendaItem} onClick={() => openBookingDirect(event.id)}>
          <div className={styles.agendaDate}>
            {event.start.toLocaleDateString(intlLocale(), { month: 'short', day: 'numeric', year: 'numeric' })}
          </div>
          <div className={styles.agendaEventCard} style={{ borderLeftColor: event.color }}>
            <div className={styles.agendaEventTime}>
              {event.start.toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true })} -
              {event.end.toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true })}
            </div>
            <div className={styles.agendaEventTitle}>{event.title}</div>
            {event.isGoogleEvent && (
              <div className={styles.agendaGcalBadge}>
                <IonIcon icon={logoGoogle} className={styles.agendaGcalIcon} />
                <span>{tr('pages.MySchedule.googleCalendar')}</span>
              </div>
            )}
            {event.clientName && <div className={styles.agendaEventClient}>{event.clientName}</div>}
            {event.location && <div className={styles.agendaEventLocation}>{event.location}</div>}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <>
      {/* Calendar accounts panel */}
      <IonModal isOpen={showCalendarPanel} onDidDismiss={() => setShowCalendarPanel(false)}>
        <IonHeader mode="ios" className="ion-no-border">
          <IonToolbar className={styles.menuToolbar}>
            <IonText className={styles.menuTitle}>{tr('pages.MySchedule.calendars')}</IonText>
            <IonButtons slot="start">
              <IonButton fill="clear" onClick={() => setShowCalendarPanel(false)}>
                <IonIcon icon={closeOutline} slot="icon-only" />
              </IonButton>
            </IonButtons>
            <IonButtons slot="end">
              <IonButton
                ref={addCalendarTriggerRef}
                id="add-calendar-gear"
                fill="clear"
                size="small"
                disabled={gcalConnecting || outlookConnecting}
                onClick={() => setShowAddCalendarMenu(v => !v)}
              >
                {gcalConnecting || outlookConnecting
                  ? <IonSpinner name="bubbles" color="primary" style={{ width: 16, height: 16 }} />
                  : <IonIcon icon={settingsOutline} slot="icon-only" style={{ fontSize: 16 }} />}
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className={styles.menuContent}>
          <IonList className={styles.menuList}>
            <div ref={addCalendarPortalRef} className={styles.pickerPortalRoot} />
            <PortalDropdown
              isOpen={showAddCalendarMenu}
              anchorRef={addCalendarTriggerRef}
              portalRef={addCalendarPortalRef}
              onClose={() => setShowAddCalendarMenu(false)}
              align="end"
              title={tr('pages.MySchedule.addCalendar')}
              options={[
                { value: 'google', label: tr('pages.MySchedule.googleAccount'), icon: logoGoogle, color: 'danger', handler: () => handleGcalConnect() },
                { value: 'outlook', label: tr('pages.MySchedule.outlookMicrosoft'), icon: mailOutline, color: 'primary', handler: () => handleOutlookConnect() },
                { value: 'ics', label: tr('pages.MySchedule.appleIcsUrl'), icon: linkOutline, color: 'success', handler: () => setShowAddIcsModal(true) },
              ]}
            />

            {gcalStatus?.accounts?.map((account) => {
              const cals = gcalAccountCalendars[account.email] ?? [];
              return (
                <div key={account.email} className={styles.gcalAccountGroup}>
                  <div className={styles.gcalAccountHeader}>
                    <div
                      className={styles.gcalAccountAvatar}
                      style={{ backgroundColor: gcalAvatarColor(account.email) }}
                    >
                      {account.email[0].toUpperCase()}
                    </div>
                    <div className={styles.gcalAccountMeta}>
                      <span className={styles.gcalAccountEmail}>{account.email}</span>
                      <span className={styles.gcalAccountSub}>{tr('pages.MySchedule.googleCalendar')}</span>
                    </div>
                    <IonButton
                      fill="clear"
                      size="small"
                      color="medium"
                      className={styles.gcalDisconnectBtn}
                      onClick={() => handleGcalDisconnect(account.email)}
                    >
                      <IonIcon icon={closeCircleOutline} slot="icon-only" />
                    </IonButton>
                  </div>

                  {cals.length === 0 ? (
                    <div className={styles.gcalCalendarRow} style={{ cursor: 'default' }}>
                      <IonSpinner name="bubbles" color="primary" style={{ width: 16, height: 16, marginLeft: 2 }} />
                      <span className={styles.gcalCalendarName} style={{ opacity: 0.5 }}>{tr('pages.MySchedule.loading')}</span>
                    </div>
                  ) : (
                    cals.map((cal) => {
                      const key = `${account.email}::${cal.id}`;
                      const isVisible = !gcalHiddenCalendars.has(key);
                      const color = cal.color ?? GOOGLE_CALENDAR_COLOR;
                      return (
                        <div
                          key={cal.id}
                          className={styles.gcalCalendarRow}
                          onClick={() => toggleCalendarVisibility(account.email, cal.id)}
                        >
                          <div
                            className={styles.gcalColorBox}
                            style={isVisible
                              ? { backgroundColor: color, borderColor: color }
                              : { backgroundColor: 'transparent', borderColor: color }
                            }
                          >
                            {isVisible && <span className={styles.gcalColorBoxCheck}>✓</span>}
                          </div>
                          <span className={styles.gcalCalendarName}>{cal.name}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              );
            })}

            {/* Outlook Calendar accounts */}
            {outlookStatus?.accounts?.map((account) => {
              const cals = outlookAccountCalendars[account.email] ?? [];
              return (
                <div key={account.email} className={styles.gcalAccountGroup}>
                  <div className={styles.gcalAccountHeader}>
                    <div
                      className={styles.gcalAccountAvatar}
                      style={{ backgroundColor: gcalAvatarColor(account.email) }}
                    >
                      {account.email[0].toUpperCase()}
                    </div>
                    <div className={styles.gcalAccountMeta}>
                      <span className={styles.gcalAccountEmail}>{account.email}</span>
                      <span className={styles.gcalAccountSub}>{tr('pages.MySchedule.outlookCalendar')}</span>
                    </div>
                    <IonButton
                      fill="clear"
                      size="small"
                      color="medium"
                      className={styles.gcalDisconnectBtn}
                      onClick={() => handleOutlookDisconnect(account.email)}
                    >
                      <IonIcon icon={closeCircleOutline} slot="icon-only" />
                    </IonButton>
                  </div>

                  {cals.length === 0 ? (
                    <div className={styles.gcalCalendarRow} style={{ cursor: 'default' }}>
                      <IonSpinner name="bubbles" color="primary" style={{ width: 16, height: 16, marginLeft: 2 }} />
                      <span className={styles.gcalCalendarName} style={{ opacity: 0.5 }}>{tr('pages.MySchedule.loading')}</span>
                    </div>
                  ) : (
                    cals.map((cal) => {
                      const key = `${account.email}::${cal.id}`;
                      const isVisible = !gcalHiddenCalendars.has(key);
                      const color = cal.color ?? '#0078D4';
                      return (
                        <div
                          key={cal.id}
                          className={styles.gcalCalendarRow}
                          onClick={() => toggleCalendarVisibility(account.email, cal.id)}
                        >
                          <div
                            className={styles.gcalColorBox}
                            style={isVisible
                              ? { backgroundColor: color, borderColor: color }
                              : { backgroundColor: 'transparent', borderColor: color }
                            }
                          >
                            {isVisible && <span className={styles.gcalColorBoxCheck}>✓</span>}
                          </div>
                          <span className={styles.gcalCalendarName}>{cal.name}</span>
                        </div>
                      );
                    })
                  )}
                </div>
              );
            })}

            {/* ICS / Apple Calendar subscriptions */}
            {icsCalendars.length > 0 && (
              <div className={styles.gcalAccountGroup}>
                <div className={styles.gcalAccountHeader} style={{ paddingBottom: 0 }}>
                  <div className={styles.gcalAccountMeta} style={{ marginLeft: 0 }}>
                    <span className={styles.gcalAccountEmail}>{tr('pages.MySchedule.subscribedCalendars')}</span>
                  </div>
                </div>
                {icsCalendars.map((cal) => {
                  const key = `ics::${cal.id}`;
                  const isVisible = !gcalHiddenCalendars.has(key);
                  return (
                    <div key={cal.id} className={styles.gcalCalendarRow} style={{ justifyContent: 'space-between' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}
                        onClick={() => toggleCalendarVisibility('ics', cal.id)}
                      >
                        <div
                          className={styles.gcalColorBox}
                          style={isVisible
                            ? { backgroundColor: cal.color, borderColor: cal.color }
                            : { backgroundColor: 'transparent', borderColor: cal.color }
                          }
                        >
                          {isVisible && <span className={styles.gcalColorBoxCheck}>✓</span>}
                        </div>
                        <span className={styles.gcalCalendarName}>{cal.name}</span>
                      </div>
                      <IonButton fill="clear" size="small" color="medium" style={{ '--padding-start': '4px', '--padding-end': '4px' }}
                        onClick={() => handleRemoveIcsCalendar(cal.id)}
                      >
                        <IonIcon icon={closeCircleOutline} slot="icon-only" />
                      </IonButton>
                    </div>
                  );
                })}
              </div>
            )}

          </IonList>
        </IonContent>
      </IonModal>

      {/* ICS / Apple Calendar — add modal */}
      <IonModal isOpen={showAddIcsModal} onDidDismiss={() => { setShowAddIcsModal(false); setIcsFormError(''); }}>
        <IonHeader>
          <IonToolbar>
            <IonTitle>{tr('pages.MySchedule.subscribeToCalendar')}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={() => setShowAddIcsModal(false)}>{tr('pages.MySchedule.close')}</IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <IonList lines="full">
            <IonItem>
              <IonLabel position="stacked">{tr('pages.MySchedule.calendarName')}</IonLabel>
              <IonInput
                value={icsFormName}
                onIonInput={(e) => setIcsFormName(e.detail.value ?? '')}
                placeholder={tr('pages.MySchedule.myCalendar')}
                clearInput
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">{tr('pages.MySchedule.icsWebcalUrl')}</IonLabel>
              <IonInput
                value={icsFormUrl}
                onIonInput={(e) => setIcsFormUrl(e.detail.value ?? '')}
                placeholder="https://… or webcal://…"
                type="url"
                clearInput
              />
            </IonItem>
            <IonItem>
              <IonLabel position="stacked">{tr('pages.MySchedule.type')}</IonLabel>
              <IonSelect value={icsFormType} onIonChange={(e) => setIcsFormType(e.detail.value)}>
                <IonSelectOption value="ics">{tr('pages.MySchedule.icsFeed')}</IonSelectOption>
                <IonSelectOption value="apple">{tr('pages.MySchedule.appleCalendar')}</IonSelectOption>
              </IonSelect>
            </IonItem>
            <IonItem>
              <IonLabel>{tr('pages.MySchedule.color')}</IonLabel>
              <input
                type="color"
                value={icsFormColor}
                onChange={(e) => setIcsFormColor(e.target.value)}
                style={{ width: 36, height: 36, border: 'none', background: 'none', cursor: 'pointer' }}
              />
            </IonItem>
          </IonList>
          {icsFormError && (
            <IonText color="danger" className="ion-padding-horizontal">
              <p style={{ fontSize: 13 }}>{icsFormError}</p>
            </IonText>
          )}
          <div className="ion-padding">
            <IonButton expand="block" onClick={handleAddIcsCalendar} disabled={icsFormSaving || !icsFormName.trim() || !icsFormUrl.trim()}>
              {icsFormSaving ? <IonSpinner name="bubbles" color="primary" /> : tr('pages.MySchedule.subscribe')}
            </IonButton>
          </div>
        </IonContent>
      </IonModal>

      <IonPage id="main-content">
        <IonHeader mode="ios" className="ion-no-border">
          <IonToolbar className={styles.toolbar}>
            <IonButtons slot="start">
              <IonButton onClick={() => router.goBack()}>
                <IonIcon icon={chevronBackOutline} />
              </IonButton>
            </IonButtons>
            <IonTitle className={styles.title}>{tr('pages.MySchedule.mySchedule')}</IonTitle>
            <IonButtons slot="end">
              <IonButton onClick={goToToday} title={tr('pages.MySchedule.today')}>
                <TodayCalendarIcon className={styles.todayIcon} />
              </IonButton>
              <IonButton
                ref={scheduleViewTriggerRef}
                id="schedule-view-btn"
                title={tr('pages.MySchedule.changeView')}
                onClick={() => setShowViewMenu(v => !v)}
              >
                <IonIcon icon={menuOutline} />
              </IonButton>
            </IonButtons>
          </IonToolbar>
        </IonHeader>

        <IonContent className={styles.content} ref={contentRef}>
          {/* Navigation */}
          {viewMode !== 'agenda' && (
            <div className={styles.monthNav}>
              <div className={styles.monthNavTop}>
                <IonButton fill="clear" onClick={() => navigatePeriod('prev')} className={styles.navButtonLeft}>
                  <IonIcon icon={chevronBackOutline} />
                </IonButton>
                <div className={styles.monthHeader}>
                  <IonText className={styles.monthText}>{formatHeader()}</IonText>
                </div>
                <IonButton fill="clear" onClick={() => navigatePeriod('next')} className={styles.navButtonRight}>
                  <IonIcon icon={chevronForwardOutline} />
                </IonButton>
              </div>
              {isRefreshing && <IonProgressBar type="indeterminate" color="primary" style={{ height: '2px' }} />}

              {/* Month view weekday headers inside sticky nav container */}
              {viewMode === 'month' && (
                <div className={styles.monthWeekDays}>
                  {weekdayNames('short').map((day) => (
                    <div key={day} className={styles.monthWeekDay}>{day}</div>
                  ))}
                </div>
              )}

              {/* Week view day headers inside sticky nav container */}
              {viewMode === 'week' && (
                <>
                  <div className={styles.dayHeaders}>
                    <div className={styles.timeGutter}></div>
                    {getWeekDaysForDate(currentDate).map((day, index) => (
                      <div
                        key={index}
                        className={`${styles.dayHeader} ${isToday(day) ? styles.today : ''}`}
                      >
                        <div className={styles.dayName}>
                          {day.toLocaleDateString(intlLocale(), { weekday: 'short' })}
                        </div>
                        <div className={styles.dayNumber}>{day.getDate()}</div>
                      </div>
                    ))}
                  </div>
                  {/* All-day events strip — always mounted, collapses via CSS when no events */}
                  <div className={`${styles.allDayRow} ${allDayRowVisible ? styles.allDayRowVisible : styles.allDayRowHidden}`}>
                    <div className={styles.allDayGutter}><span className={styles.allDayLabel}>{tr('pages.MySchedule.allDay')}</span></div>
                    {getWeekDaysForDate(currentDate).map((day, i) => {
                      const chips = allEvents.filter(e => e.isAllDay && toLocalDateStr(e.start) === toLocalDateStr(day));
                      return (
                        <div key={i} className={styles.allDayCell}>
                          {chips.map(e => (
                            <span key={e.id} className={styles.allDayChip} style={{ backgroundColor: e.color }} onClick={() => openBookingDirect(e.id)}>{e.title}</span>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}

              {/* Day view single day header inside sticky nav container */}
              {viewMode === 'day' && (
                <IonText className={styles.dayHeaderTrackViewport}>
                  <IonText ref={dayHeaderTrackRef} className={styles.dayHeaderTrack}>
                    {([-1, 0, 1] as const).map((offset) => {
                      const day = getDateForOffset(offset);
                      return (
                        <IonText
                          key={offset}
                          className={`${styles.dayHeaderSpan} ${isToday(day) ? styles.today : ''}`}
                        >
                          <IonText className={styles.dayDateStack}>
                            <IonText className={styles.dayName}>
                              {day.toLocaleDateString(intlLocale(), { weekday: 'short' })}
                            </IonText>
                            <IonText className={styles.dayNumber}>{day.getDate()}</IonText>
                          </IonText>
                          <IonText className={styles.allDayArea}>
                            {allEvents
                              .filter(e => e.isAllDay && toLocalDateStr(e.start) === toLocalDateStr(day))
                              .map(e => (
                                <span key={e.id} className={styles.allDayChip} style={{ backgroundColor: e.color }} onClick={() => openBookingDirect(e.id)}>{e.title}</span>
                              ))
                            }
                          </IonText>
                        </IonText>
                      );
                    })}
                  </IonText>
                </IonText>
              )}
            </div>
          )}

          {/* Render appropriate view with Swiper for month/week/day, plain view for agenda */}
          {viewMode === 'agenda' ? (
            renderAgendaView()
          ) : (
            <Swiper
              className={styles.calendarSwiper}
              style={(viewMode === 'week' || viewMode === 'day') ? { height: `${24 * SLOT_PX + 20}px` } : undefined}
              onSwiper={(swiper) => {
                swiperRef.current = swiper;
                setDayHeaderTrackTransition(0);
                // Defer initial sync — at onSwiper time Swiper's width is 0 (no layout yet),
                // so swiper.translate = 0 and would incorrectly show the prev-day slot.
                requestAnimationFrame(() => syncDayHeaderTrackToSwiper(swiper));

                swiper.on('setTranslate', (s) => {
                  syncDayHeaderTrackToSwiper(s);
                });

                swiper.on('setTransition', (_s, duration) => {
                  setDayHeaderTrackTransition(duration);
                });
              }}
              onSlideChange={handleSlideChange}
              initialSlide={1}
              allowTouchMove={true}
              speed={300}
            >
              <SwiperSlide key="prev">{renderViewContent(-1)}</SwiperSlide>
              <SwiperSlide key="current">{renderViewContent(0)}</SwiperSlide>
              <SwiperSlide key="next">{renderViewContent(1)}</SwiperSlide>
            </Swiper>
          )}

        {/* Floating Action Button */}
        <IonFab className={styles.scheduleFab}>
          <CustomLiquidGlass
            className={styles.glassFab}
            style={{ borderRadius: '50%' }}
          >
            <IonFabButton>
              <IonIcon icon={addOutline} />
            </IonFabButton>
          </CustomLiquidGlass>
        </IonFab>
      </IonContent>
    </IonPage>

    {/* View switcher dropdown — rendered after IonPage so trigger button exists in DOM */}
    <div ref={scheduleViewPortalRef} className={styles.pickerPortalRoot} />
    <PortalDropdown
      isOpen={showViewMenu}
      anchorRef={scheduleViewTriggerRef}
      portalRef={scheduleViewPortalRef}
      onClose={() => setShowViewMenu(false)}
      align="end"
      title={tr('pages.MySchedule.view')}
      options={[
        { value: 'month',   label: tr('pages.MySchedule.month'),   icon: appsOutline,        color: viewMode === 'month'  ? 'primary' : 'medium', endIcon: viewMode === 'month'  ? checkmarkOutline : undefined, handler: () => selectView('month') },
        { value: 'week',    label: tr('pages.MySchedule.week'),    icon: gridOutline,         color: viewMode === 'week'   ? 'primary' : 'medium', endIcon: viewMode === 'week'   ? checkmarkOutline : undefined, handler: () => selectView('week') },
        { value: 'day',     label: tr('pages.MySchedule.day'),     icon: squareOutline,       color: viewMode === 'day'    ? 'primary' : 'medium', endIcon: viewMode === 'day'    ? checkmarkOutline : undefined, handler: () => selectView('day') },
        { value: 'agenda',  label: tr('pages.MySchedule.agenda'),  icon: reorderThreeOutline, color: viewMode === 'agenda' ? 'primary' : 'medium', endIcon: viewMode === 'agenda' ? checkmarkOutline : undefined, handler: () => selectView('agenda') },
        { value: 'refresh', label: tr('pages.MySchedule.refresh'), icon: syncOutline,         color: 'medium', handler: handleRefresh },
        { value: 'calendars', label: tr('pages.MySchedule.calendars'), icon: calendarOutline,  color: 'medium', handler: () => setShowCalendarPanel(true) },
      ]}
    />

    {/* Day sheet – slides up when a month day is tapped */}
    <IonModal
      isOpen={showDaySheet}
      onDidDismiss={() => setShowDaySheet(false)}
      breakpoints={[0, 0.4, 0.85]}
      initialBreakpoint={0.4}
      handle
      className={styles.daySheetModal}
    >
      <IonContent className={styles.daySheetContent}>
        <div className={styles.daySheetHeader}>
          <IonText className={styles.daySheetTitle}>
            {selectedDay?.toLocaleDateString(intlLocale(), { weekday: 'long', month: 'long', day: 'numeric' })}
          </IonText>
          {selectedDay && getEventsForDate(selectedDay).length === 0 && (
            <IonText className={styles.daySheetEmpty}>{tr('pages.MySchedule.nothingScheduled')}</IonText>
          )}
        </div>
              <div className={styles.daySheetList}>
          {selectedDay && getEventsForDate(selectedDay)
            .sort((a, b) => a.start.getTime() - b.start.getTime())
            .map((event) => (
              <div
                key={event.id}
                className={`${styles.miniBookingCard} ${event.isGoogleEvent ? styles.googleEventCard : ''}`}
                onClick={() => {
                  if (event.isGoogleEvent && event.gcalRaw) {
                    openGcalDetail(event.gcalRaw);
                    setShowDaySheet(false);
                    return;
                  }
                  const booking = allEvents.find(e => e.id === event.id);
                  if (booking) DataStorageService.storeData(`booking-${event.id}`, booking, false);
                  setSelectedBookingId(event.id);
                  setShowDaySheet(false);
                  setShowBookingDetails(true);
                }}
              >
                <div className={styles.miniBookingAccent} style={{ backgroundColor: event.color }} />
                <div className={styles.miniBookingBody}>
                  <div className={styles.miniBookingTitle}>{event.title}</div>
                  {event.isGoogleEvent && (
                    <div className={styles.miniBookingGcalBadge}>
                      <IonIcon icon={logoGoogle} className={styles.miniBookingGcalIcon} />
                      <span>{tr('pages.MySchedule.googleCalendar')}</span>
                    </div>
                  )}
                  <div className={styles.miniBookingMeta}>
                    <IonIcon icon={timeOutline} className={styles.miniBookingIcon} />
                    <span>
                      {event.start.toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true })}
                      {' – '}
                      {event.end.toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true })}
                    </span>
                  </div>
                  {event.clientName && (
                    <div className={styles.miniBookingMeta}>
                      <IonIcon icon={personOutline} className={styles.miniBookingIcon} />
                      <span>{event.clientName}</span>
                    </div>
                  )}
                  {event.location && (
                    <div className={styles.miniBookingMeta}>
                      <IonIcon icon={locationOutline} className={styles.miniBookingIcon} />
                      <span>{event.location}</span>
                    </div>
                  )}
                </div>
                {!event.isGoogleEvent && (
                  <IonIcon icon={chevronForwardOutlineIcon} className={styles.miniBookingChevron} />
                )}
                {event.isGoogleEvent && (
                  <IonIcon icon={chevronForwardOutlineIcon} className={styles.miniBookingChevron} style={{ color: '#1a73e8' }} />
                )}
              </div>
            ))
          }
        </div>
      </IonContent>
    </IonModal>

    {showBookingDetails && selectedBookingId && (
      <BookingDetails
        bookingId={selectedBookingId}
        isOpen={showBookingDetails}
        hidePayment
        onClose={() => {
          setShowBookingDetails(false);
          setSelectedBookingId(undefined);
        }}
      />
    )}

    {/* Google Calendar Event Detail / Edit Modal */}
    <IonModal
      isOpen={showGcalDetail}
      onDidDismiss={() => { setShowGcalDetail(false); setSelectedGcalEvent(null); setGcalEditMode(false); setGcalConfirmDelete(false); }}
      breakpoints={[0, 0.95]}
      initialBreakpoint={0.95}
      handle
      expandToScroll={false}
      className={styles.daySheetModal}
    >
      <IonContent className={styles.daySheetContent}>
        {selectedGcalEvent && (
          <>
            {/* ── HEADER ── */}
            <div className={styles.gcalDetailHeader} style={{ borderLeftColor: selectedGcalEvent.calendarColor || GOOGLE_CALENDAR_COLOR }}>
              <div className={styles.gcalDetailBadge}>
                <IonIcon icon={logoGoogle} />
                <span>{selectedGcalEvent.calendarName || tr('pages.MySchedule.googleCalendar')}</span>
              </div>
              <div className={styles.gcalDetailHeaderRow}>
                <div className={styles.gcalDetailTitle}>
                  {gcalEditMode
                    ? <IonInput className={styles.gcalTitleInput} value={gcalEditTitle} onIonInput={(e) => setGcalEditTitle(e.detail.value ?? '')} placeholder={tr('pages.MySchedule.eventTitle')} />
                    : selectedGcalEvent.title
                  }
                </div>
                <div className={styles.gcalHeaderActions}>
                  {!gcalEditMode && (
                    <>
                      {(selectedGcalEvent?.organizerSelf !== false ||
                        gcalStatus?.accounts?.some((a) => a.email === selectedGcalEvent?.organizerEmail)) && (
                        <IonButton fill="clear" size="small" className={styles.gcalEditBtn} onClick={() => setGcalEditMode(true)}>
                          <IonIcon icon={createOutline} slot="icon-only" />
                        </IonButton>
                      )}
                      <IonButton fill="clear" size="small" className={styles.gcalDeleteBtn} onClick={() => setGcalConfirmDelete(true)}>
                        <IonIcon icon={trashOutline} slot="icon-only" />
                      </IonButton>
                    </>
                  )}
                  {gcalEditMode && (
                    <IonButton fill="clear" size="small" className={styles.gcalCancelBtn} onClick={() => { setGcalEditMode(false); setGcalConfirmDelete(false); }}>{tr('pages.MySchedule.cancel')}
                    </IonButton>
                  )}
                </div>
              </div>
            </div>

            {/* ── DELETE CONFIRM BANNER ── */}
            {gcalConfirmDelete && !gcalEditMode && (
              <div className={styles.gcalDeleteConfirm}>
                <span>{tr('pages.MySchedule.deleteThisEvent')}</span>
                <div className={styles.gcalDeleteConfirmBtns}>
                  <IonButton fill="clear" size="small" onClick={() => setGcalConfirmDelete(false)}>{tr('pages.MySchedule.keep')}</IonButton>
                  <IonButton
                    size="small"
                    color="danger"
                    disabled={gcalDeleting}
                    onClick={async () => {
                      if (!selectedGcalEvent?.calendarId || !selectedGcalEvent?.rawEventId) return;
                      setGcalDeleting(true);
                      try {
                        await googleCalendarService.deleteEvent(selectedGcalEvent.calendarId!, selectedGcalEvent.rawEventId!, selectedGcalEvent.accountEmail!);
                        setGcalEvents((prev) => prev.filter((ev) => ev.gcalRaw?.rawEventId !== selectedGcalEvent.rawEventId));
                        setGcalToast({ message: tr('pages.MySchedule.eventDeleted'), color: 'success' });
                        setShowGcalDetail(false);
                        setSelectedGcalEvent(null);
                      } catch {
                        setGcalToast({ message: tr('pages.MySchedule.failedToDeleteTryAgain'), color: 'danger' });
                      } finally {
                        setGcalDeleting(false);
                        setGcalConfirmDelete(false);
                      }
                    }}
                  >
                    {gcalDeleting ? <IonSpinner name="bubbles" color="primary" /> : tr('pages.MySchedule.delete')}
                  </IonButton>
                </div>
              </div>
            )}

            {/* ── VIEW MODE ── */}
            {!gcalEditMode && (
              <div className={styles.gcalDetailBody}>
                {/* Time */}
                <div className={styles.gcalDetailRow}>
                  <IonIcon icon={timeOutline} className={styles.gcalDetailIcon} />
                  <div>
                    {selectedGcalEvent.isAllDay ? (
                      <span>All day &middot; {new Date(selectedGcalEvent.start).toLocaleDateString(intlLocale(), { weekday: 'long', month: 'long', day: 'numeric' })}</span>
                    ) : (
                      <span>
                        {new Date(selectedGcalEvent.start).toLocaleDateString(intlLocale(), { weekday: 'long', month: 'long', day: 'numeric' })}
                        <br />
                        {new Date(selectedGcalEvent.start).toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true })}
                        {' – '}
                        {new Date(selectedGcalEvent.end).toLocaleTimeString(intlLocale(), { hour: 'numeric', minute: '2-digit', hour12: true })}
                      </span>
                    )}
                    {selectedGcalEvent.recurrence && selectedGcalEvent.recurrence.length > 0 && (
                      <div className={styles.gcalRepeatBadge}>{tr('pages.MySchedule.repeating')}</div>
                    )}
                  </div>
                </div>
                {/* Location */}
                {selectedGcalEvent.location && (
                  <div className={styles.gcalDetailRow}>
                    <IonIcon icon={locationOutline} className={styles.gcalDetailIcon} />
                    <a
                      className={styles.gcalLocationLink}
                      href={`https://maps.google.com/?q=${encodeURIComponent(selectedGcalEvent.location)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {selectedGcalEvent.location}
                    </a>
                  </div>
                )}
                {/* Attendees */}
                {selectedGcalEvent.attendees && selectedGcalEvent.attendees.length > 0 && (
                  <div className={styles.gcalDetailRow}>
                    <IonIcon icon={peopleOutline} className={styles.gcalDetailIcon} />
                    <div className={styles.gcalAttendeesView}>
                      {selectedGcalEvent.attendees.map((a) => (
                        <div key={a.email} className={styles.gcalAttendeeRow}>
                          <span className={styles.gcalAttendeeEmail}>{a.displayName || a.email}</span>
                          {a.responseStatus && (
                            <span className={`${styles.gcalRsvp} ${styles[`rsvp_${a.responseStatus}`]}`}>
                              {a.responseStatus === 'accepted' ? '✓' : a.responseStatus === 'declined' ? '✗' : a.responseStatus === 'tentative' ? '?' : '–'}
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Status / Visibility */}
                <div className={styles.gcalDetailRow}>
                  <IonIcon icon={eyeOutline} className={styles.gcalDetailIcon} />
                  <span>
                    {selectedGcalEvent.transparency === 'transparent' ? tr('pages.MySchedule.free') : tr('pages.MySchedule.busy')}
                    {selectedGcalEvent.visibility && selectedGcalEvent.visibility !== 'default'
                      ? ` · ${selectedGcalEvent.visibility.charAt(0).toUpperCase() + selectedGcalEvent.visibility.slice(1)}`
                      : ''}
                  </span>
                </div>
                {/* Reminders */}
                {selectedGcalEvent.reminders && !selectedGcalEvent.reminders.useDefault && selectedGcalEvent.reminders.overrides && selectedGcalEvent.reminders.overrides.length > 0 && (
                  <div className={styles.gcalDetailRow}>
                    <IonIcon icon={notificationsOutline} className={styles.gcalDetailIcon} />
                    <div>
                      {selectedGcalEvent.reminders.overrides.map((r, i) => (
                        <div key={i}>{r.method === 'email' ? tr('pages.MySchedule.email') : tr('pages.MySchedule.notification')} · {r.minutes < 60 ? `${r.minutes} min` : `${r.minutes / 60}h`}{tr('pages.MySchedule.before')}</div>
                      ))}
                    </div>
                  </div>
                )}
                {/* Description */}
                {selectedGcalEvent.description && (
                  <div className={styles.gcalDetailRow}>
                    <IonIcon icon={listOutline} className={styles.gcalDetailIcon} />
                    <div className={styles.gcalDetailDescription}>{selectedGcalEvent.description}</div>
                  </div>
                )}
              </div>
            )}

            {/* ── RSVP row — one row per connected account that is an attendee ── */}
            {!gcalEditMode && (gcalStatus?.accounts ?? [])
              .filter((acct) =>
                selectedGcalEvent.attendees?.some((a) => a.email === acct.email) ||
                acct.email === selectedGcalEvent.accountEmail
              )
              .map((acct) => {
                const attendee = selectedGcalEvent.attendees?.find((a) => a.email === acct.email)
                  ?? selectedGcalEvent.attendees?.find((a) => a.self && selectedGcalEvent.accountEmail === acct.email);
                const current = attendee?.responseStatus;
                return (
                  <div key={acct.email} className={styles.gcalRsvpRow}>
                    <span className={styles.gcalRsvpLabel}>{tr('pages.MySchedule.going')} <small>({acct.email})</small></span>
                    {(['accepted', 'tentative', 'declined'] as const).map((status) => (
                      <IonButton
                        key={status}
                        fill={current === status ? 'solid' : 'outline'}
                        size="small"
                        className={styles.gcalRsvpBtn}
                        onClick={async () => {
                          if (!selectedGcalEvent.calendarId || !selectedGcalEvent.rawEventId) return;
                          try {
                            await googleCalendarService.updateEvent({
                              calendarId: selectedGcalEvent.calendarId!,
                              rawEventId: selectedGcalEvent.rawEventId!,
                              title: selectedGcalEvent.title,
                              start: selectedGcalEvent.start,
                              end: selectedGcalEvent.end,
                              isAllDay: selectedGcalEvent.isAllDay,
                              accountEmail: acct.email,
                              organizerEmail: selectedGcalEvent.organizerEmail,
                              attendees: (selectedGcalEvent.attendees ?? []).map((a) =>
                                a.email === acct.email ? { ...a, responseStatus: status } : a
                              ),
                            });
                            fetchGcalEvents();
                          } catch (err) {
                            console.error('[GCal] RSVP error:', err);
                          }
                        }}
                      >
                        {status === 'accepted' ? tr('pages.MySchedule.yes') : status === 'tentative' ? tr('pages.MySchedule.maybe') : tr('pages.MySchedule.no')}
                      </IonButton>
                    ))}
                  </div>
                );
              })}

            {/* ── EDIT MODE ── */}
            {gcalEditMode && (
              <div className={styles.gcalEditBody}>

                {/* ── Date/Time group ── */}
                <IonList lines="inset" className={styles.gcalEditList}>

                  {/* All day */}
                  <IonItem className={styles.gcalEditItem}>
                    <IonLabel>{tr('pages.MySchedule.allDay2')}</IonLabel>
                    <IonToggle
                      slot="end"
                      checked={gcalEditIsAllDay}
                      onIonChange={(e) => {
                        const on = e.detail.checked;
                        setGcalEditIsAllDay(on);
                        if (on) {
                          setGcalEditStart(gcalEditStart.substring(0, 10));
                          setGcalEditEnd(gcalEditEnd.substring(0, 10));
                        } else {
                          setGcalEditStart((gcalEditStart.substring(0, 10) || new Date().toISOString().substring(0, 10)) + 'T09:00');
                          setGcalEditEnd((gcalEditEnd.substring(0, 10) || new Date().toISOString().substring(0, 10)) + 'T10:00');
                        }
                      }}
                    />
                  </IonItem>

                  {/* Start */}
                  <IonItem className={styles.gcalEditItem}>
                    <div className={styles.gcalDateRow}>
                      <IonLabel className={styles.gcalStackedLabel}>{tr('pages.MySchedule.start')}</IonLabel>
                      <div className={styles.gcalDateTimeInputs}>
                        <div id="gcal-start-date" className={styles.gcalDateSection} onClick={() => setGcalStartDateOpen(true)}>
                          <IonInput
                            type="text"
                            readonly
                            className={styles.gcalDateInput}
                            value={formatDateDisplay(gcalEditStart)}
                          />
                          <IonButton fill="clear" size="small" className={styles.gcalDateIconBtn}>
                            <IonIcon icon={calendarOutline} slot="icon-only" />
                          </IonButton>
                        </div>
                        <IonPopover
                          trigger="gcal-start-date"
                          isOpen={gcalStartDateOpen}
                          onDidDismiss={() => setGcalStartDateOpen(false)}
                          className={styles.gcalDatePopover}
                          side="bottom"
                          alignment="start"
                        >
                          <IonDatetime locale={intlLocale()}
                            presentation="date"
                            preferWheel={false}
                            value={gcalEditStart.substring(0, 10)}
                            onIonChange={(e) => {
                              const d = (e.detail.value as string)?.substring(0, 10) ?? '';
                              if (gcalEditIsAllDay) { setGcalEditStart(d); }
                              else { setGcalEditStart(d + 'T' + (gcalEditStart.substring(11, 16) || '09:00')); }
                              setGcalStartDateOpen(false);
                            }}
                          />
                        </IonPopover>
                        {!gcalEditIsAllDay && (
                          <>
                            <div id="gcal-start-time" className={styles.gcalTimeSection} onClick={() => setGcalStartTimeOpen(true)}>
                              <IonInput type="text" readonly className={styles.gcalTimeInput} value={formatTimeDisplay(gcalEditStart)} />
                              <IonButton fill="clear" size="small" className={styles.gcalDateIconBtn}>
                                <IonIcon icon={timeOutline} slot="icon-only" />
                              </IonButton>
                            </div>
                            <IonPopover trigger="gcal-start-time" isOpen={gcalStartTimeOpen} onDidDismiss={() => setGcalStartTimeOpen(false)} className={styles.gcalTimePopover} side="bottom" alignment="start">
                              <GcalTimePicker value={gcalEditStart.substring(11, 16) || '09:00'} onSelect={(t) => { setGcalEditStart(gcalEditStart.substring(0, 10) + 'T' + t); setGcalStartTimeOpen(false); }} onClose={() => setGcalStartTimeOpen(false)} />
                            </IonPopover>
                          </>
                        )}
                      </div>
                    </div>
                  </IonItem>

                  {/* End */}
                  <IonItem className={styles.gcalEditItem}>
                    <div className={styles.gcalDateRow}>
                      <IonLabel className={styles.gcalStackedLabel}>{tr('pages.MySchedule.end')}</IonLabel>
                      <div className={styles.gcalDateTimeInputs}>
                        <div id="gcal-end-date" className={styles.gcalDateSection} onClick={() => setGcalEndDateOpen(true)}>
                          <IonInput
                            type="text"
                            readonly
                            className={styles.gcalDateInput}
                            value={formatDateDisplay(gcalEditEnd)}
                          />
                          <IonButton fill="clear" size="small" className={styles.gcalDateIconBtn}>
                            <IonIcon icon={calendarOutline} slot="icon-only" />
                          </IonButton>
                        </div>
                        <IonPopover
                          trigger="gcal-end-date"
                          isOpen={gcalEndDateOpen}
                          onDidDismiss={() => setGcalEndDateOpen(false)}
                          className={styles.gcalDatePopover}
                          side="bottom"
                          alignment="start"
                        >
                          <IonDatetime locale={intlLocale()}
                            presentation="date"
                            preferWheel={false}
                            value={gcalEditEnd.substring(0, 10)}
                            onIonChange={(e) => {
                              const d = (e.detail.value as string)?.substring(0, 10) ?? '';
                              if (gcalEditIsAllDay) { setGcalEditEnd(d); }
                              else { setGcalEditEnd(d + 'T' + (gcalEditEnd.substring(11, 16) || '10:00')); }
                              setGcalEndDateOpen(false);
                            }}
                          />
                        </IonPopover>
                        {!gcalEditIsAllDay && (
                          <>
                            <div id="gcal-end-time" className={styles.gcalTimeSection} onClick={() => setGcalEndTimeOpen(true)}>
                              <IonInput type="text" readonly className={styles.gcalTimeInput} value={formatTimeDisplay(gcalEditEnd)} />
                              <IonButton fill="clear" size="small" className={styles.gcalDateIconBtn}>
                                <IonIcon icon={timeOutline} slot="icon-only" />
                              </IonButton>
                            </div>
                            <IonPopover trigger="gcal-end-time" isOpen={gcalEndTimeOpen} onDidDismiss={() => setGcalEndTimeOpen(false)} className={styles.gcalTimePopover} side="bottom" alignment="start">
                              <GcalTimePicker value={gcalEditEnd.substring(11, 16) || '10:00'} onSelect={(t) => { setGcalEndTimeOpen(false); setGcalEditEnd(gcalEditEnd.substring(0, 10) + 'T' + t); }} onClose={() => setGcalEndTimeOpen(false)} />
                            </IonPopover>
                          </>
                        )}
                      </div>
                    </div>
                  </IonItem>

                  {/* Repeat */}
                  <IonItem className={styles.gcalEditItem}>
                    <IonSelect
                      label={tr('pages.MySchedule.doesNotRepeat')}
                      labelPlacement="fixed"
                      value={gcalEditRepeat}
                      onIonChange={(e) => setGcalEditRepeat(e.detail.value)}
                      interface="action-sheet"
                    >
                      <IonSelectOption value="NONE">{tr('pages.MySchedule.doesNotRepeat')}</IonSelectOption>
                      <IonSelectOption value="RRULE:FREQ=DAILY">{tr('pages.MySchedule.everyDay')}</IonSelectOption>
                      <IonSelectOption value="RRULE:FREQ=WEEKLY">{tr('pages.MySchedule.everyWeek')}</IonSelectOption>
                      <IonSelectOption value="RRULE:FREQ=WEEKLY;BYDAY=MO,TU,WE,TH,FR">{tr('pages.MySchedule.everyWeekdayMonFri')}</IonSelectOption>
                      <IonSelectOption value="RRULE:FREQ=MONTHLY">{tr('pages.MySchedule.everyMonth')}</IonSelectOption>
                      <IonSelectOption value="RRULE:FREQ=YEARLY">{tr('pages.MySchedule.everyYear')}</IonSelectOption>
                    </IonSelect>
                  </IonItem>

                </IonList>

                {/* ── Event details group ── */}
                <IonList lines="inset" className={styles.gcalEditList}>

                  {/* Location */}
                  <IonItem className={styles.gcalEditItem}>
                    <IonIcon icon={locationOutline} slot="start" className={styles.gcalEditIcon} />
                    <IonInput
                      placeholder={tr('pages.MySchedule.addLocation')}
                      value={gcalEditLocation}
                      onIonInput={(e) => setGcalEditLocation(e.detail.value ?? '')}
                    />
                  </IonItem>

                  {/* Notification */}
                  <IonItem className={styles.gcalEditItem}>
                    <IonIcon icon={notificationsOutline} slot="start" className={styles.gcalEditIcon} />
                    <div className={styles.gcalNotifRow}>
                      {gcalEditReminderMinutes === null ? (
                        <IonButton fill="clear" className={styles.gcalAddNotifBtn}
                          onClick={() => { setGcalEditReminderMinutes(10); setGcalEditReminderValue('10'); setGcalEditReminderUnit('minutes'); }}>{tr('pages.MySchedule.addNotification')}
                        </IonButton>
                      ) : (
                        <div className={styles.gcalReminderRow}>
                          <IonSelect
                            className={styles.gcalReminderMethod}
                            value={gcalEditReminderMethod}
                            onIonChange={(e) => setGcalEditReminderMethod(e.detail.value)}
                            interface="popover"
                          >
                            <IonSelectOption value="popup">{tr('pages.MySchedule.notification')}</IonSelectOption>
                            <IonSelectOption value="email">{tr('pages.MySchedule.email')}</IonSelectOption>
                          </IonSelect>
                          <IonInput
                            type="number"
                            className={styles.gcalReminderNumber}
                            value={gcalEditReminderValue}
                            min="0"
                            max="999"
                            onIonInput={(e) => {
                              const v = e.detail.value ?? '10';
                              setGcalEditReminderValue(v);
                              const n = parseInt(v, 10);
                              if (!isNaN(n)) {
                                const multiplier = gcalEditReminderUnit === 'days' ? 1440 : gcalEditReminderUnit === 'hours' ? 60 : 1;
                                setGcalEditReminderMinutes(n * multiplier);
                              }
                            }}
                          />
                          <IonSelect
                            className={styles.gcalReminderUnit}
                            value={gcalEditReminderUnit}
                            onIonChange={(e) => {
                              const unit = e.detail.value as 'minutes' | 'hours' | 'days';
                              setGcalEditReminderUnit(unit);
                              const n = parseInt(gcalEditReminderValue, 10) || 0;
                              const multiplier = unit === 'days' ? 1440 : unit === 'hours' ? 60 : 1;
                              setGcalEditReminderMinutes(n * multiplier);
                            }}
                            interface="popover"
                          >
                            <IonSelectOption value="minutes">{tr('pages.MySchedule.minutes')}</IonSelectOption>
                            <IonSelectOption value="hours">{tr('pages.MySchedule.hours')}</IonSelectOption>
                            <IonSelectOption value="days">{tr('pages.MySchedule.days')}</IonSelectOption>
                          </IonSelect>
                          <IonButton fill="clear" size="small" className={styles.gcalNotifClear}
                            onClick={() => setGcalEditReminderMinutes(null)}>
                            <IonIcon icon={closeOutline} slot="icon-only" />
                          </IonButton>
                        </div>
                      )}
                    </div>
                  </IonItem>

                  {/* Calendar */}
                  {gcalCalendarList.length > 0 && (
                    <IonItem className={styles.gcalEditItem}>
                      <IonIcon icon={calendarOutline} slot="start" className={styles.gcalEditIcon} />
                      <IonSelect
                        value={gcalEditCalendarId}
                        onIonChange={(e) => setGcalEditCalendarId(e.detail.value)}
                        interface="action-sheet"
                      >
                        {gcalCalendarList.map((cal) => (
                          <IonSelectOption key={cal.id} value={cal.id}>{cal.name}</IonSelectOption>
                        ))}
                      </IonSelect>
                    </IonItem>
                  )}

                  {/* Status + Visibility on same row */}
                  <IonItem className={styles.gcalEditItem}>
                    <IonIcon icon={eyeOutline} slot="start" className={styles.gcalEditIcon} />
                    <div className={styles.gcalStatusVisRow}>
                      <IonSelect
                        className={styles.gcalEditInputHalf}
                        value={gcalEditTransparency}
                        onIonChange={(e) => setGcalEditTransparency(e.detail.value)}
                        interface="popover"
                      >
                        <IonSelectOption value="opaque">{tr('pages.MySchedule.busy')}</IonSelectOption>
                        <IonSelectOption value="transparent">{tr('pages.MySchedule.free')}</IonSelectOption>
                      </IonSelect>
                      <IonSelect
                        className={styles.gcalEditInputHalf}
                        value={gcalEditVisibility}
                        onIonChange={(e) => setGcalEditVisibility(e.detail.value)}
                        interface="popover"
                      >
                        <IonSelectOption value="default">{tr('pages.MySchedule.defaultVisibility')}</IonSelectOption>
                        <IonSelectOption value="public">{tr('pages.MySchedule.public')}</IonSelectOption>
                        <IonSelectOption value="private">{tr('pages.MySchedule.private')}</IonSelectOption>
                        <IonSelectOption value="confidential">{tr('pages.MySchedule.confidential')}</IonSelectOption>
                      </IonSelect>
                    </div>
                  </IonItem>

                  {/* Color */}
                  <IonItem className={styles.gcalEditItem}>
                    <div className={styles.gcalColorSection}>
                      <IonLabel className={styles.gcalStackedLabel}>{tr('pages.MySchedule.color')}</IonLabel>
                      <div className={styles.gcalColorPicker}>
                        {[
                          { id: '', label: 'Calendar default', hex: selectedGcalEvent.calendarColor || '#4285F4' },
                          { id: '1', label: 'Tomato', hex: '#D50000' },
                          { id: '2', label: 'Flamingo', hex: '#E67C73' },
                          { id: '3', label: 'Tangerine', hex: '#F4511E' },
                          { id: '4', label: 'Banana', hex: '#F6BF26' },
                          { id: '5', label: 'Sage', hex: '#33B679' },
                          { id: '6', label: 'Basil', hex: '#0B8043' },
                          { id: '7', label: 'Peacock', hex: '#039BE5' },
                          { id: '8', label: 'Blueberry', hex: '#3F51B5' },
                          { id: '9', label: 'Lavender', hex: '#7986CB' },
                          { id: '10', label: 'Grape', hex: '#8E24AA' },
                          { id: '11', label: 'Graphite', hex: '#616161' },
                        ].map((c) => (
                          <button
                            key={c.id}
                            title={c.label}
                            className={`${styles.gcalColorSwatch} ${gcalEditColorId === c.id ? styles.gcalColorSwatchSelected : ''}`}
                            style={{ background: c.hex }}
                            onClick={() => setGcalEditColorId(c.id)}
                          />
                        ))}
                      </div>
                    </div>
                  </IonItem>

                  {/* Description */}
                  <IonItem className={styles.gcalEditItem} lines="none">
                    <IonIcon icon={listOutline} slot="start" className={styles.gcalEditIcon} />
                    <IonTextarea
                      placeholder={tr('pages.MySchedule.addDescription')}
                      value={gcalEditDescription}
                      onIonInput={(e) => setGcalEditDescription(e.detail.value ?? '')}
                      autoGrow
                      rows={3}
                    />
                  </IonItem>

                </IonList>

                {/* ── Guests group ── */}
                <IonList lines="inset" className={styles.gcalEditList}>

                  {/* Add guest input */}
                  <IonItem className={styles.gcalEditItem}>
                    <IonIcon icon={peopleOutline} slot="start" className={styles.gcalEditIcon} />
                    <IonInput
                      placeholder={tr('pages.MySchedule.addGuests')}
                      type="email"
                      value={gcalEditNewGuest}
                      onIonInput={(e) => setGcalEditNewGuest(e.detail.value ?? '')}
                      onKeyUp={(e) => {
                        if (e.key === 'Enter' && gcalEditNewGuest.includes('@')) {
                          setGcalEditAttendees((prev) => [...prev, { email: gcalEditNewGuest }]);
                          setGcalEditNewGuest('');
                        }
                      }}
                    />
                    {gcalEditNewGuest.includes('@') && (
                      <IonButton slot="end" fill="clear" size="small"
                        onClick={() => { setGcalEditAttendees((prev) => [...prev, { email: gcalEditNewGuest }]); setGcalEditNewGuest(''); }}>{tr('pages.MySchedule.add')}
                      </IonButton>
                    )}
                  </IonItem>

                  {/* Guest chips */}
                  {gcalEditAttendees.length > 0 && (
                    <IonItem lines="none" className={styles.gcalEditItem}>
                      <div className={styles.gcalAttendeeChips}>
                        {gcalEditAttendees.map((a, i) => (
                          <IonChip key={i} className={styles.gcalAttendeeChip}>
                            <IonLabel>{a.email}</IonLabel>
                            <IonIcon icon={closeOutline} onClick={() => setGcalEditAttendees((prev) => prev.filter((_, idx) => idx !== i))} />
                          </IonChip>
                        ))}
                      </div>
                    </IonItem>
                  )}

                  {/* Guest permissions */}
                  {gcalEditAttendees.length > 0 && (
                    <>
                      <IonItem className={styles.gcalPermHeader}>
                        <IonLabel className={styles.gcalStackedLabel}>{tr('pages.MySchedule.guestPermissions')}</IonLabel>
                      </IonItem>
                      <IonItem lines="none" className={styles.gcalEditItem}>
                        <IonCheckbox slot="start" checked={gcalEditGuestsCanModify} onIonChange={(e) => setGcalEditGuestsCanModify(e.detail.checked)} />
                        <IonLabel>{tr('pages.MySchedule.modifyEvent')}</IonLabel>
                      </IonItem>
                      <IonItem lines="none" className={styles.gcalEditItem}>
                        <IonCheckbox slot="start" checked={gcalEditGuestsCanInvite} onIonChange={(e) => setGcalEditGuestsCanInvite(e.detail.checked)} />
                        <IonLabel>{tr('pages.MySchedule.inviteOthers')}</IonLabel>
                      </IonItem>
                      <IonItem lines="none" className={styles.gcalEditItem}>
                        <IonCheckbox slot="start" checked={gcalEditGuestsCanSeeOthers} onIonChange={(e) => setGcalEditGuestsCanSeeOthers(e.detail.checked)} />
                        <IonLabel>{tr('pages.MySchedule.seeGuestList')}</IonLabel>
                      </IonItem>
                    </>
                  )}

                </IonList>

                {/* Save */}
                <IonButton
                  expand="block"
                  className={styles.gcalSaveButton}
                  disabled={gcalSaving || !gcalEditTitle.trim()}
                  onClick={async () => {
                    if (!selectedGcalEvent?.calendarId || !selectedGcalEvent?.rawEventId) return;
                    setGcalSaving(true);
                    try {
                      const toIso = (val: string) =>
                        gcalEditIsAllDay ? val : (val ? new Date(val).toISOString() : '');
                      const recurrenceParam = gcalEditRepeat === 'NONE' ? [] : [gcalEditRepeat];
                      // Compute final reminder minutes from value+unit
                      let finalReminderMinutes = gcalEditReminderMinutes;
                      if (gcalEditReminderMinutes !== null) {
                        const n = parseInt(gcalEditReminderValue, 10) || 0;
                        const mult = gcalEditReminderUnit === 'days' ? 1440 : gcalEditReminderUnit === 'hours' ? 60 : 1;
                        finalReminderMinutes = n * mult;
                      }
                      // Only send start/end if the user changed them
                      const origStart = selectedGcalEvent.isAllDay
                        ? selectedGcalEvent.start.substring(0, 10)
                        : toDatetimeLocal(selectedGcalEvent.start);
                      const origEnd = selectedGcalEvent.isAllDay
                        ? (() => {
                            const d = new Date(selectedGcalEvent.end.substring(0, 10) + 'T00:00:00');
                            d.setDate(d.getDate() - 1);
                            return d.toISOString().substring(0, 10);
                          })()
                        : toDatetimeLocal(selectedGcalEvent.end);
                      const startChanged = gcalEditStart !== origStart;
                      const endChanged = gcalEditEnd !== origEnd;
                      console.log('[GCal] save debug', { gcalEditStart, gcalEditEnd, origStart, origEnd, startChanged, endChanged, isAllDay: gcalEditIsAllDay });
                      const updated = await googleCalendarService.updateEvent({
                        calendarId: selectedGcalEvent.calendarId,
                        rawEventId: selectedGcalEvent.rawEventId,
                        title: gcalEditTitle,
                        ...(startChanged || endChanged ? { start: toIso(gcalEditStart), end: toIso(gcalEditEnd), isAllDay: gcalEditIsAllDay, timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone } : {}),
                        location: gcalEditLocation,
                        description: gcalEditDescription,
                        colorId: gcalEditColorId || undefined,
                        transparency: gcalEditTransparency,
                        visibility: gcalEditVisibility,
                        attendees: gcalEditAttendees,
                        guestsCanModify: gcalEditGuestsCanModify,
                        guestsCanInviteOthers: gcalEditGuestsCanInvite,
                        guestsCanSeeOtherGuests: gcalEditGuestsCanSeeOthers,
                        reminderMinutes: finalReminderMinutes ?? undefined,
                        reminderMethod: finalReminderMinutes !== null ? gcalEditReminderMethod : undefined,
                        recurrence: recurrenceParam,
                        newCalendarId: gcalEditCalendarId !== selectedGcalEvent.calendarId ? gcalEditCalendarId : undefined,
                        organizerEmail: selectedGcalEvent.organizerEmail,
                        accountEmail: selectedGcalEvent.accountEmail!,
                      });
                      const GCAL_COLOR_MAP: Record<string, string> = { '1': '#D50000', '2': '#E67C73', '3': '#F4511E', '4': '#F6BF26', '5': '#33B679', '6': '#0B8043', '7': '#039BE5', '8': '#3F51B5', '9': '#7986CB', '10': '#8E24AA', '11': '#616161' };
                      setGcalEvents((prev) => prev.map((ev) =>
                        ev.gcalRaw?.rawEventId === selectedGcalEvent.rawEventId
                          ? {
                              ...ev,
                              title: updated.title,
                              start: new Date(updated.start),
                              end: new Date(updated.end),
                              location: updated.location,
                              color: (updated.colorId ? GCAL_COLOR_MAP[updated.colorId] : undefined) || ev.gcalRaw?.calendarColor || GOOGLE_CALENDAR_COLOR,
                              gcalRaw: ev.gcalRaw ? {
                                ...ev.gcalRaw,
                                title: updated.title,
                                start: updated.start,
                                end: updated.end,
                                location: updated.location,
                                description: updated.description,
                                colorId: updated.colorId,
                                transparency: updated.transparency,
                                visibility: updated.visibility,
                                isAllDay: updated.isAllDay ?? ev.gcalRaw.isAllDay,
                                calendarId: updated.calendarId ?? ev.gcalRaw.calendarId,
                                rawEventId: updated.rawEventId ?? ev.gcalRaw.rawEventId,
                              } : ev.gcalRaw,
                            }
                          : ev
                      ));
                      setGcalToast({ message: tr('pages.MySchedule.eventSaved'), color: 'success' });
                      setShowGcalDetail(false);
                      setSelectedGcalEvent(null);
                      setGcalEditMode(false);
                    } catch (err) {
                      console.error('[GCal] updateEvent error:', err);
                      setGcalToast({ message: tr('pages.MySchedule.failedToSavePleaseTryAgain'), color: 'danger' });
                    } finally {
                      setGcalSaving(false);
                    }
                  }}
                >
                  {gcalSaving ? <IonSpinner name="bubbles" color="primary" /> : tr('pages.MySchedule.saveChanges')}
                </IonButton>
              </div>
            )}
          </>
        )}
      </IonContent>
    </IonModal>

    <IonToast
      isOpen={!!gcalToast}
      message={gcalToast?.message}
      color={gcalToast?.color}
      duration={2500}
      onDidDismiss={() => setGcalToast(null)}
      position="top"
    />
  </>
  );
};

export default MySchedule;
